import * as THREE from 'three';

export type AimAssistTarget = { x: number; y: number; z: number };

const AIM_ASSIST_MAX_ANGLE_DEG = 40;
const AIM_ASSIST_MAX_DIST = 25;
const AIM_ASSIST_STRENGTH = 0.15;

/** Mobile touch overlay — virtual stick, look pad, action buttons. */
export class FpsTouchControls {
  private readonly root: HTMLDivElement;
  private stickActive = false;
  private stickId = -1;
  private stickOrigin = { x: 0, y: 0 };
  private stickDelta = { x: 0, y: 0 };
  private lookActive = false;
  private lookId = -1;
  private lookLast = { x: 0, y: 0 };
  private moveKeys = new Set<string>();
  private fireHeld = false;
  private readonly touchDevice: boolean;

  constructor(
    private mount: HTMLElement,
    private callbacks: {
      onMoveKey: (code: string, down: boolean) => void;
      onLook: (dx: number, dy: number) => void;
      onFire: (down: boolean) => void;
      onReload: () => void;
      onJump: () => void;
      onBuild: () => void;
    },
  ) {
    this.touchDevice = this.detectTouchDevice();
    this.root = document.createElement('div');
    this.root.id = 'touch-controls';
    this.root.style.cssText =
      'position:fixed;inset:0;z-index:45;pointer-events:none;display:none;touch-action:none;';
    this.root.innerHTML = `
      <div id="touch-stick-zone" style="position:absolute;left:0;bottom:0;width:44%;height:52%;pointer-events:auto">
        <div id="touch-stick-base" style="position:absolute;left:26%;bottom:26%;width:104px;height:104px;border-radius:50%;background:rgba(255,255,255,0.08);border:2px solid rgba(255,255,255,0.18)"></div>
        <div id="touch-stick-knob" style="position:absolute;left:26%;bottom:26%;width:104px;height:104px;margin:-38px 0 0 -38px;border-radius:50%;background:rgba(46,230,255,0.35);border:2px solid #2ee6ff;transform:translate(0,0)"></div>
      </div>
      <div id="touch-look-zone" style="position:absolute;right:0;top:0;width:52%;height:100%;pointer-events:auto;clip-path:polygon(0 0,100% 0,100% 100%,38% 100%,38% 58%,0 58%)"></div>
      <button type="button" id="touch-reload" style="position:absolute;right:108px;bottom:92px;width:58px;height:58px;border-radius:50%;border:2px solid #b388ff;background:rgba(140,100,255,0.32);color:#fff;font:800 10px system-ui;pointer-events:auto;touch-action:manipulation">RLD</button>
      <button type="button" id="touch-fire" style="position:absolute;right:18px;bottom:86px;width:80px;height:80px;border-radius:50%;border:2px solid #ff6b6b;background:rgba(255,80,80,0.38);color:#fff;font:800 14px system-ui;pointer-events:auto;touch-action:manipulation">FIRE</button>
      <button type="button" id="touch-jump" style="position:absolute;right:112px;bottom:22px;width:64px;height:64px;border-radius:50%;border:2px solid #8cf;background:rgba(80,160,255,0.34);color:#fff;font:800 12px system-ui;pointer-events:auto;touch-action:manipulation">JUMP</button>
      <button type="button" id="touch-build" style="position:absolute;right:18px;bottom:18px;width:58px;height:58px;border-radius:50%;border:2px solid #f1c40f;background:rgba(241,196,15,0.28);color:#fff;font:800 11px system-ui;pointer-events:auto;touch-action:manipulation">BLD</button>
    `;
    mount.appendChild(this.root);

    if (this.touchDevice) {
      this.root.style.display = 'block';
    }

    const stickZone = this.root.querySelector('#touch-stick-zone') as HTMLDivElement;
    const lookZone = this.root.querySelector('#touch-look-zone') as HTMLDivElement;
    const fireBtn = this.root.querySelector('#touch-fire') as HTMLButtonElement;
    const reloadBtn = this.root.querySelector('#touch-reload') as HTMLButtonElement;
    const jumpBtn = this.root.querySelector('#touch-jump') as HTMLButtonElement;
    const buildBtn = this.root.querySelector('#touch-build') as HTMLButtonElement;

    stickZone.addEventListener('touchstart', this.onStickStart, { passive: false });
    stickZone.addEventListener('touchmove', this.onStickMove, { passive: false });
    stickZone.addEventListener('touchend', this.onStickEnd, { passive: false });
    stickZone.addEventListener('touchcancel', this.onStickEnd, { passive: false });

    lookZone.addEventListener('touchstart', this.onLookStart, { passive: false });
    lookZone.addEventListener('touchmove', this.onLookMove, { passive: false });
    lookZone.addEventListener('touchend', this.onLookEnd, { passive: false });
    lookZone.addEventListener('touchcancel', this.onLookEnd, { passive: false });

    fireBtn.addEventListener('touchstart', (e) => {
      e.preventDefault();
      this.fireHeld = true;
      this.callbacks.onFire(true);
    });
    fireBtn.addEventListener('touchend', (e) => {
      e.preventDefault();
      this.fireHeld = false;
      this.callbacks.onFire(false);
    });
    fireBtn.addEventListener('touchcancel', (e) => {
      e.preventDefault();
      this.fireHeld = false;
      this.callbacks.onFire(false);
    });
    reloadBtn.addEventListener('touchstart', (e) => {
      e.preventDefault();
      this.callbacks.onReload();
    });
    jumpBtn.addEventListener('touchstart', (e) => {
      e.preventDefault();
      this.callbacks.onJump();
    });
    buildBtn.addEventListener('touchstart', (e) => {
      e.preventDefault();
      this.callbacks.onBuild();
    });
  }

  isTouchDevice(): boolean {
    return this.touchDevice;
  }

  isFireHeld(): boolean {
    return this.fireHeld;
  }

  /** Nearest enemy within cone + distance for aim assist. */
  static findAimAssistTarget(
    camera: THREE.PerspectiveCamera,
    eyeX: number,
    eyeY: number,
    eyeZ: number,
    targets: AimAssistTarget[],
    maxAngleDeg = AIM_ASSIST_MAX_ANGLE_DEG,
    maxDist = AIM_ASSIST_MAX_DIST,
  ): AimAssistTarget | null {
    const forward = new THREE.Vector3();
    camera.getWorldDirection(forward);
    const maxAngle = (maxAngleDeg * Math.PI) / 180;
    let best: { target: AimAssistTarget; dist: number } | null = null;

    for (const target of targets) {
      const dx = target.x - eyeX;
      const dy = target.y - eyeY;
      const dz = target.z - eyeZ;
      const dist = Math.hypot(dx, dy, dz);
      if (dist > maxDist || dist < 0.5) continue;
      const dir = new THREE.Vector3(dx, dy, dz).normalize();
      const angle = Math.acos(Math.min(1, Math.max(-1, forward.dot(dir))));
      if (angle > maxAngle) continue;
      if (!best || dist < best.dist) best = { target, dist };
    }

    return best?.target ?? null;
  }

  /** Apply fractional aim nudge toward a world point (strength 0–1). */
  static applyAimNudge(
    camera: THREE.PerspectiveCamera,
    eyeX: number,
    eyeY: number,
    eyeZ: number,
    target: AimAssistTarget,
    strength = AIM_ASSIST_STRENGTH,
  ): { yawDelta: number; pitchDelta: number } {
    const dx = target.x - eyeX;
    const dy = target.y - eyeY;
    const dz = target.z - eyeZ;
    const distH = Math.hypot(dx, dz);
    const desiredYaw = Math.atan2(dx, dz);
    const desiredPitch = -Math.atan2(dy, Math.max(0.001, distH));

    const euler = new THREE.Euler().setFromQuaternion(camera.quaternion, 'YXZ');
    let yawDelta = desiredYaw - euler.y;
    yawDelta = ((yawDelta + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
    const pitchDelta = desiredPitch - euler.x;

    return {
      yawDelta: yawDelta * strength,
      pitchDelta: pitchDelta * strength,
    };
  }

  static getAimAssistStrength(): number {
    return AIM_ASSIST_STRENGTH;
  }

  private detectTouchDevice(): boolean {
    if (typeof window === 'undefined') return false;
    return (
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      window.matchMedia('(pointer: coarse)').matches
    );
  }

  private setMoveFromStick(dx: number, dy: number): void {
    const threshold = 0.22;
    const want = new Set<string>();
    if (dy < -threshold) want.add('KeyW');
    if (dy > threshold) want.add('KeyS');
    if (dx < -threshold) want.add('KeyA');
    if (dx > threshold) want.add('KeyD');

    for (const k of ['KeyW', 'KeyA', 'KeyS', 'KeyD']) {
      const on = want.has(k);
      if (on !== this.moveKeys.has(k)) {
        this.callbacks.onMoveKey(k, on);
        if (on) this.moveKeys.add(k);
        else this.moveKeys.delete(k);
      }
    }
  }

  private clearMove(): void {
    for (const k of [...this.moveKeys]) {
      this.callbacks.onMoveKey(k, false);
    }
    this.moveKeys.clear();
    const knob = this.root.querySelector('#touch-stick-knob') as HTMLDivElement;
    if (knob) knob.style.transform = 'translate(0px,0px)';
  }

  private onStickStart = (e: TouchEvent): void => {
    e.preventDefault();
    if (this.stickActive) return;
    const t = e.changedTouches[0];
    if (!t) return;
    this.stickActive = true;
    this.stickId = t.identifier;
    this.stickOrigin = { x: t.clientX, y: t.clientY };
    this.stickDelta = { x: 0, y: 0 };
  };

  private onStickMove = (e: TouchEvent): void => {
    if (!this.stickActive) return;
    e.preventDefault();
    const t = [...e.changedTouches].find((x) => x.identifier === this.stickId);
    if (!t) return;
    const dx = Math.max(-1, Math.min(1, (t.clientX - this.stickOrigin.x) / 52));
    const dy = Math.max(-1, Math.min(1, (t.clientY - this.stickOrigin.y) / 52));
    this.stickDelta = { x: dx, y: dy };
    const knob = this.root.querySelector('#touch-stick-knob') as HTMLDivElement;
    if (knob) knob.style.transform = `translate(${dx * 28}px,${dy * 28}px)`;
    this.setMoveFromStick(dx, dy);
  };

  private onStickEnd = (e: TouchEvent): void => {
    const t = [...e.changedTouches].find((x) => x.identifier === this.stickId);
    if (!t && this.stickActive) return;
    this.stickActive = false;
    this.stickId = -1;
    this.clearMove();
  };

  private onLookStart = (e: TouchEvent): void => {
    if (this.lookActive) return;
    const t = e.changedTouches[0];
    if (!t) return;
    this.lookActive = true;
    this.lookId = t.identifier;
    this.lookLast = { x: t.clientX, y: t.clientY };
  };

  private onLookMove = (e: TouchEvent): void => {
    if (!this.lookActive) return;
    e.preventDefault();
    const t = [...e.changedTouches].find((x) => x.identifier === this.lookId);
    if (!t) return;
    const dx = t.clientX - this.lookLast.x;
    const dy = t.clientY - this.lookLast.y;
    this.lookLast = { x: t.clientX, y: t.clientY };
    this.callbacks.onLook(dx, dy);
  };

  private onLookEnd = (): void => {
    this.lookActive = false;
    this.lookId = -1;
  };

  dispose(): void {
    this.fireHeld = false;
    this.clearMove();
    this.root.remove();
  }
}
